import { Sparkles, ThumbsUp, ThumbsDown, Target, Users, Compass, TrendingUp, DollarSign, Clock, Type, Image as ImageIcon, Search, CalendarClock, Lightbulb, AlertTriangle, Award, Flag } from "lucide-react";

// Legacy Portuguese key → new English key. Kept to gracefully handle any old
// cached analyses persisted before the English migration.
const LEGACY_KEY_MAP = {
  summary: "resumo_geral",
  strengths: "pontos_fortes",
  weaknesses: "pontos_fracos",
  content_type: "tipo_conteudo",
  target_audience: "publico_alvo",
  niche: "nicho",
  growth_probability: "probabilidade_crescimento",
  growth_reason: "justificativa_crescimento",
  monetization_potential: "potencial_monetizacao",
  monetization_reason: "justificativa_monetizacao",
  consistency: "consistencia",
  title_quality: "qualidade_titulos",
  thumbnail_quality: "qualidade_miniaturas",
  seo_quality: "qualidade_seo",
  upload_frequency: "frequencia_postagem",
  best_upload_times: "melhores_horarios",
  view_growth_suggestions: "sugestoes_visualizacoes",
  subscriber_growth_suggestions: "sugestoes_inscritos",
  opportunities: "oportunidades",
  risks: "riscos",
  conclusion: "conclusao",
};

const normalizeDiagnosis = (d) => {
  const out = {};
  for (const [key, legacy] of Object.entries(LEGACY_KEY_MAP)) {
    out[key] = d[key] ?? d[legacy];
  }
  return out;
};

const Card = ({ icon: Icon, title, children, testId, span = "" }) => (
  <div data-testid={testId} className={`card-flat p-6 ${span}`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-primary" aria-hidden="true" />
      <div className="overline">{title}</div>
    </div>
    <div className="text-sm leading-relaxed text-foreground/90">{children}</div>
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-1.5">
    {(items || []).map((it) => (
      <li key={it} className="flex gap-2 text-sm">
        <span className="text-primary mt-1.5 w-1 h-1 rounded-full bg-primary shrink-0" aria-hidden="true" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const SectionHeader = () => (
  <div className="flex items-center gap-3 mb-6">
    <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/40 flex items-center justify-center">
      <Sparkles className="w-5 h-5 text-primary" />
    </div>
    <div>
      <h2 className="text-3xl font-black tracking-tight">AI Diagnosis</h2>
      <div className="overline mt-1">Powered by GPT-5.2 · Creator strategist</div>
    </div>
  </div>
);

const Fallback = ({ summary }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/40 flex items-center justify-center">
        <Sparkles className="w-5 h-5 text-primary" />
      </div>
      <h2 className="text-3xl font-black tracking-tight">AI Diagnosis</h2>
    </div>
    <div data-testid="ai-diagnosis-fallback" className="card-flat p-8 border-amber-500/30 bg-amber-500/5">
      <p className="text-base">{summary || "AI diagnosis is temporarily unavailable. Please try again shortly."}</p>
    </div>
  </section>
);

export default function AIDiagnosis({ diagnosis, status }) {
  // Loading state while backend generates diagnosis in background.
  if (status === "pending" && !diagnosis) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-testid="ai-diagnosis-loading">
        <SectionHeader />
        <div className="card-flat p-8 border-primary/20 bg-gradient-to-br from-card to-secondary/20">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-full border-2 border-primary/30 border-t-primary animate-spin" aria-hidden="true" />
            <div>
              <div className="font-semibold">Generating AI diagnosis…</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                GPT-5.2 is analyzing the channel. This usually takes 30–60 seconds.
              </div>
            </div>
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-16 rounded-lg bg-secondary/50 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!diagnosis) return null;
  const d = normalizeDiagnosis(diagnosis);

  const hasFullDiagnosis = d.strengths || d.content_type || d.niche;
  if (!hasFullDiagnosis) return <Fallback summary={d.summary} />;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
      <SectionHeader />

      <div data-testid="ai-diagnosis" className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div data-testid="diag-summary" className="md:col-span-12 card-flat p-8 bg-gradient-to-br from-card to-secondary/30 border-primary/20">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-primary" />
            <div className="overline">Overall summary</div>
          </div>
          <p className="text-lg leading-relaxed whitespace-pre-line">{d.summary}</p>
        </div>

        <Card icon={ThumbsUp} title="Strengths" testId="diag-strengths" span="md:col-span-6">
          <List items={d.strengths} />
        </Card>
        <Card icon={ThumbsDown} title="Weaknesses" testId="diag-weaknesses" span="md:col-span-6">
          <List items={d.weaknesses} />
        </Card>

        <Card icon={Target} title="Content type" testId="diag-content-type" span="md:col-span-4">{d.content_type}</Card>
        <Card icon={Users} title="Target audience" testId="diag-audience" span="md:col-span-4">{d.target_audience}</Card>
        <Card icon={Compass} title="Niche" testId="diag-niche" span="md:col-span-4">{d.niche}</Card>

        <Card icon={TrendingUp} title="Growth potential" testId="diag-growth" span="md:col-span-6">
          <div className="text-2xl font-black text-primary mb-2">{d.growth_probability}</div>
          <div className="text-muted-foreground">{d.growth_reason}</div>
        </Card>
        <Card icon={DollarSign} title="Monetization potential" testId="diag-monetization" span="md:col-span-6">
          <div className="text-2xl font-black text-emerald-400 mb-2">{d.monetization_potential}</div>
          <div className="text-muted-foreground">{d.monetization_reason}</div>
        </Card>

        <Card icon={Clock} title="Upload consistency" testId="diag-consistency" span="md:col-span-6">{d.consistency}</Card>
        <Card icon={CalendarClock} title="Upload frequency" testId="diag-frequency" span="md:col-span-6">{d.upload_frequency}</Card>

        <Card icon={Type} title="Title quality" testId="diag-titles" span="md:col-span-4">{d.title_quality}</Card>
        <Card icon={ImageIcon} title="Thumbnail quality" testId="diag-thumbs" span="md:col-span-4">{d.thumbnail_quality}</Card>
        <Card icon={Search} title="SEO quality" testId="diag-seo" span="md:col-span-4">{d.seo_quality}</Card>

        <Card icon={Clock} title="Best upload times" testId="diag-times" span="md:col-span-12">{d.best_upload_times}</Card>

        <Card icon={Lightbulb} title="How to grow views" testId="diag-sugg-views" span="md:col-span-6">
          <List items={d.view_growth_suggestions} />
        </Card>
        <Card icon={Lightbulb} title="How to grow subscribers" testId="diag-sugg-subs" span="md:col-span-6">
          <List items={d.subscriber_growth_suggestions} />
        </Card>

        <Card icon={Award} title="Opportunities" testId="diag-opportunities" span="md:col-span-6">
          <List items={d.opportunities} />
        </Card>
        <Card icon={AlertTriangle} title="Risks" testId="diag-risks" span="md:col-span-6">
          <List items={d.risks} />
        </Card>

        <Card icon={Flag} title="Recommendations & conclusion" testId="diag-conclusion" span="md:col-span-12">
          <p className="text-base leading-relaxed">{d.conclusion}</p>
        </Card>
      </div>
    </section>
  );
}
